// library.js — Carton Library API client (FE Integration PR ③).
//
// Every /library/* route requires Authorization: Bearer <access> (even before
// the auth flag flips), so all calls go through authFetch (PR ② §5: refresh →
// retry-once on 401 invalid_token). Errors carry the backend's detail.code so
// the UI can drive §4 affordances. PDF-only in this build — the parametric
// route (/library/cartons/parametric) is intentionally not implemented.

import { authFetch } from './auth';
import { API_BASE } from './config';
import { patchBody, uploadQuery } from './libraryDuplicates';
import { browseQuery } from './superuserLibrary';

export class LibraryApiError extends Error {
  constructor(message, status, code, body) {
    super(message);
    this.name = 'LibraryApiError';
    this.status = status;
    this.code = code || null;
    this.body = body || null;
  }
}

async function readError(resp) {
  let body = null;
  try { body = await resp.json(); } catch { /* non-JSON */ }
  const code = body?.detail?.code || body?.code || null;
  // detect-die-style 400s put the human message directly in detail (a string)
  // or detail.message — surface it verbatim (§4: "it names the file").
  const msg =
    (typeof body?.detail === 'string' && body.detail) ||
    body?.detail?.message ||
    body?.message ||
    code ||
    `Request failed (${resp.status})`;
  return new LibraryApiError(msg, resp.status, code, body);
}

/**
 * List the caller's own + org-shared cartons (live only, newest first, ≤500).
 * Filtering/search is client-side. Does NOT fire the usage metric.
 */
export async function listCartons({ apiBase = API_BASE, include, signal } = {}) {
  // include='drafts' (PR ⑧ §3) returns the caller's drafts alongside library
  // cartons; omitted, the list is library-only (the default curated view).
  const q = include ? `?include=${encodeURIComponent(include)}` : '';
  const resp = await authFetch(`${apiBase}/library/cartons${q}`, { signal });
  if (!resp.ok) throw await readError(resp);
  const data = await resp.json();
  return Array.isArray(data) ? data : (data?.items || data?.cartons || []);
}

/**
 * A superuser's Library (V2 #97): GET /library/browse → { view, items, total,
 * page, page_size }. Send `view` ONLY when the user has just chosen one — the
 * backend stores and records it; omitted, it answers with the remembered view
 * (superuserLibrary.js has the rules). Members are refused 403: they use
 * listCartons.
 */
export async function browseCartons({ apiBase = API_BASE, signal, ...query } = {}) {
  const resp = await authFetch(`${apiBase}/library/browse${browseQuery(query)}`, { signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Every organisation, for a superuser's "One organisation" view (the Control
 *  Center's list, which admits superusers). [{ id, name, is_active }] */
export async function listOrganisations({ apiBase = API_BASE, signal } = {}) {
  const resp = await authFetch(`${apiBase}/admin/orgs`, { signal });
  if (!resp.ok) throw await readError(resp);
  const data = await resp.json();
  return (Array.isArray(data) ? data : []).map(({ id, name, is_active }) => ({ id, name, is_active }));
}

/**
 * Full record for one carton. FIRES THE USAGE METRIC — call it when the user
 * actually loads a carton, not for list rendering.
 */
export async function getCarton({ apiBase = API_BASE, id, signal }) {
  const resp = await authFetch(`${apiBase}/library/cartons/${encodeURIComponent(id)}`, { signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Upload one PDF into the library. `name` optional (defaults to filename on the
 * backend). 201 → the record. Idempotent by bytes: re-uploading a live
 * byte-identical file returns 201 with the EXISTING record — callers key on
 * `id` (compare against what they already have) to detect "already in library".
 */
/**
 * Upload one PDF into the library (§5.2). Die detection runs once, here.
 *
 * **Idempotent (§5.3):** re-uploading a byte-identical file you already have
 * returns **201 with the existing record**. Key on `id` to detect "already in
 * the library" — never on the status code, which is 201 either way.
 *
 * `draft` maps to the forthcoming `?draft=true` (backend PR ⑧), which keeps
 * scratch drops out of the curated library. It is additive: omitting it is
 * exactly today's behaviour, so this signature does not change when it lands.
 *
 * V2-AC-26 (#87): a library upload (not a draft) may be refused 409
 * `duplicate_carton`; `allowDuplicate` sends it again to save anyway.
 */
export async function uploadCarton({ apiBase = API_BASE, file, name, draft = false, allowDuplicate = false, signal }) {
  const form = new FormData();
  form.append('file', file, file.name);
  const q = uploadQuery({ name, draft, allowDuplicate });
  const resp = await authFetch(`${apiBase}/library/cartons${q}`, { method: 'POST', body: form, signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Fetch the original PDF bytes for lossless export — feed the returned File
 * into the export path exactly like a fresh upload. 404 no_source for
 * parametric cartons (not applicable in this build, but handle defensively).
 * Returns a File so downstream FormData(name) works unchanged.
 */
export async function getCartonSource({ apiBase = API_BASE, id, filename, signal }) {
  const resp = await authFetch(`${apiBase}/library/cartons/${encodeURIComponent(id)}/source`, { signal });
  if (!resp.ok) throw await readError(resp);
  const blob = await resp.blob();
  const safe = filename && /\.pdf$/i.test(filename) ? filename : `${filename || id}.pdf`;
  return new File([blob], safe, { type: 'application/pdf' });
}

/**
 * Rename and/or reshare. Pass { name } and/or { visibility: 'private'|'org' }.
 * Geometry is immutable (replacement is a new upload). 422 bad_visibility if
 * visibility isn't private|org; 403 forbidden if not owner/admin. Promoting or
 * sharing may be refused 409 `duplicate_carton` (V2-AC-26); `allowDuplicate`
 * does it anyway.
 */
export async function patchCarton({ apiBase = API_BASE, id, name, visibility, origin, allowDuplicate = false, signal }) {
  const patch = patchBody({ name, visibility, origin, allowDuplicate });
  const resp = await authFetch(`${apiBase}/library/cartons/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Promote a scratch draft into the curated library (PR ⑧ §4). One-way: only
 * `origin: 'library'` is valid. Sharing to the org is a SEPARATE visibility
 * PATCH afterwards — promotion alone lands it private.
 */
export async function promoteCarton({ apiBase = API_BASE, id, allowDuplicate = false, signal } = {}) {
  return patchCarton({ apiBase, id, origin: 'library', allowDuplicate, signal });
}

/** PR ⑧ §6: every carton now carries origin: 'library' | 'draft'. */
export function isDraftCarton(carton) {
  return carton?.origin === 'draft';
}

/** Soft-delete: disappears from lists/loads; running/past plans unaffected. 204. */
export async function deleteCarton({ apiBase = API_BASE, id, signal }) {
  const resp = await authFetch(`${apiBase}/library/cartons/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
  if (!resp.ok && resp.status !== 204) throw await readError(resp);
  return true;
}

// §3, narrowed by V2 #93: show edit/share/delete only on the caller's OWN
// organisation's cartons, to the owner or an admin — superuser or not. The rule
// lives in libraryAccess.js (pure, tested); re-exported so importers keep using
// './library'.
export { canManageCarton } from './libraryAccess';

/** Best-effort dimensions string for a card, in mm (UI converts via fmtLen). */
export function cartonDims(carton) {
  const f = carton?.flat_size;
  if (f && (f.w_mm != null || f.h_mm != null)) return { w: f.w_mm ?? 0, h: f.h_mm ?? 0 };
  const b = carton?.geometry?.bbox_mm;
  if (Array.isArray(b) && b.length === 4) return { w: b[2] - b[0], h: b[3] - b[1] };
  return null;
}
